import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Printer, X } from 'lucide-react';

const PrintableForm = ({ open, onOpenChange, printData }) => {
  const [isPrinting, setIsPrinting] = useState(false);
  
  if (!printData) return null;

  const handlePrint = () => {
    // Simple, reliable print approach
    if (isPrinting) {
      console.log('Print already in progress');
      return;
    }
    
    setIsPrinting(true);
    console.log('Starting print process...');
    
    // Use browser's native print with CSS media queries
    window.print();
    
    // Reset state after a short delay
    setTimeout(() => {
      setIsPrinting(false);
      console.log('Print process completed');
    }, 1000);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderForm = () => {
    switch (printData.type) {
      case 'ban':
        return <BanForm />;
      case 'eviction':
        return <EvictionForm />;
      case 'departure':
      default:
        return <DepartureForm />;
    }
  };

  const BanForm = () => (
    <div className="print-content" style={{
      width: '7.5in',
      height: '8in',
      maxWidth: '7.5in',
      maxHeight: '8in',
      fontFamily: 'Arial, sans-serif',
      fontSize: '8pt',
      lineHeight: '1.0',
      padding: '0.2in',
      margin: '0',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}>
      
      {/* Header Section */}
      <div style={{textAlign: 'center', marginBottom: '0.1in'}}>
        <h1 style={{fontSize: '14pt', margin: '0', fontWeight: 'bold', lineHeight: '1'}}>
          OFFICIAL TRESPASS NOTICE
        </h1>
        <h2 style={{fontSize: '11pt', margin: '1pt 0 0 0', fontWeight: 'bold', lineHeight: '1'}}>
          GOOD SAMARITAN MINISTRIES
        </h2>
      </div>

      {/* Basic Info Section */}
      <div style={{marginBottom: '0.08in', fontSize: '8pt', lineHeight: '1.0'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '2pt'}}>
          <span><strong>Resident:</strong> {printData.resident.first_name} {printData.resident.last_name}</span>
          <span><strong>Date:</strong> {printData.data.ban_date}</span>
        </div>
        <div>
          <span><strong>Staff:</strong> {printData.data.staff_name}</span>
        </div>
      </div>

      {/* Main Warning Box */}
      <div style={{
        border: '2pt solid black',
        padding: '0.08in',
        margin: '0.08in 0',
        textAlign: 'center',
        backgroundColor: '#f9f9f9'
      }}>
        <p style={{margin: '0', fontWeight: 'bold', fontSize: '9pt', lineHeight: '1.0'}}>
          YOU ARE BANNED FROM ALL PROPERTY AND SERVICES OF GOOD SAMARITAN MINISTRIES.
        </p>
        <p style={{margin: '2pt 0 0 0', fontWeight: 'bold', fontSize: '9pt', lineHeight: '1.0'}}>
          IF YOU RETURN, POLICE WILL BE CALLED AND YOU WILL BE ARRESTED.
        </p>
        <p style={{margin: '2pt 0 0 0', fontWeight: 'bold', fontSize: '9pt', lineHeight: '1.0'}}>
          SUPERVISOR APPROVAL REQUIRED FOR RE-ENTRY.
        </p>
      </div>

      {/* Reason Section */}
      <div style={{marginBottom: '0.08in', fontSize: '8pt'}}>
        <p style={{margin: '0', lineHeight: '1.0'}}>
          <strong>Reason:</strong> {printData.data.ban_reason}
        </p>
        {printData.data.ban_notes && (
          <p style={{margin: '2pt 0 0 0', lineHeight: '1.0'}}>
            <strong>Notes:</strong> {printData.data.ban_notes}
          </p>
        )}
      </div>

      {/* Signatures Section */}
      <div style={{marginTop: 'auto'}}>
        <p style={{
          textAlign: 'center',
          fontWeight: 'bold',
          marginBottom: '0.08in',
          fontSize: '9pt',
          lineHeight: '1'
        }}>
          REQUIRED SIGNATURES
        </p>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          height: '0.6in'
        }}>
          {/* Staff Signature */}
          <div style={{width: '30%', textAlign: 'center'}}>
            <div style={{
              borderBottom: '1pt solid black',
              height: '0.3in',
              marginBottom: '2pt'
            }}></div>
            <p style={{margin: '0', fontSize: '7pt', fontWeight: 'bold', lineHeight: '1'}}>
              STAFF SIGNATURE
            </p>
            <p style={{margin: '0', fontSize: '6pt', lineHeight: '1'}}>
              {printData.data.staff_name}
            </p>
          </div>
          
          {/* Police Signature */}
          <div style={{width: '30%', textAlign: 'center'}}>
            <div style={{
              borderBottom: '1pt solid black',
              height: '0.3in',
              marginBottom: '2pt'
            }}></div>
            <p style={{margin: '0', fontSize: '7pt', fontWeight: 'bold', lineHeight: '1'}}>
              POLICE SIGNATURE
            </p>
            <p style={{margin: '0', fontSize: '6pt', lineHeight: '1'}}>
              Badge #: _______
            </p>
          </div>
          
          {/* Supervisor Signature */}
          <div style={{width: '30%', textAlign: 'center'}}>
            <div style={{
              borderBottom: '1pt solid black',
              height: '0.3in',
              marginBottom: '2pt'
            }}></div>
            <p style={{margin: '0', fontSize: '7pt', fontWeight: 'bold', lineHeight: '1'}}>
              SUPERVISOR
            </p>
            <p style={{margin: '0', fontSize: '6pt', lineHeight: '1'}}>
              Name: _______
            </p>
          </div>
        </div>
      </div>

    </div>
  );

  const EvictionForm = () => (
    <div className="print-friendly space-y-6">
      <div className="text-center border-b-2 border-black pb-4 mb-6">
        <h1 className="text-2xl font-bold">OFFICIAL EVICTION NOTICE</h1>
        <p className="text-lg mt-2">LEGAL DOCUMENT - SHELTER TERMINATION</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="font-bold text-lg mb-3 border-b border-gray-400">RESIDENT INFORMATION</h3>
          <div className="space-y-2">
            <p><strong>Name:</strong> {printData.resident.first_name} {printData.resident.last_name}</p>
            <p><strong>Intake Date:</strong> {formatDate(printData.resident.intake_date)}</p>
            <p><strong>Length of Stay:</strong> {printData.resident.stay_length_days} days</p>
            <p><strong>Room Assignment:</strong> {printData.resident.room_id || 'Not Assigned'}</p>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-3 border-b border-gray-400">EVICTION DETAILS</h3>
          <div className="space-y-2">
            <p><strong>Eviction Date:</strong> {printData.data.eviction_date}</p>
            <p><strong>Staff Member:</strong> {printData.data.staff_name}</p>
            <p><strong>Eviction Reason:</strong> {printData.data.eviction_reason?.replace('_', ' ').toUpperCase()}</p>
            <p><strong>Status:</strong> EVICTED - IMMEDIATE TERMINATION</p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-bold text-lg mb-3 border-b border-gray-400">DETAILED DESCRIPTION OF VIOLATION(S)</h3>
        <div className="border border-gray-400 p-4 min-h-32 bg-gray-50">
          <p className="whitespace-pre-wrap font-medium">{printData.data.eviction_details}</p>
        </div>
      </div>

      {printData.data.eviction_notes && (
        <div className="mt-6">
          <h3 className="font-bold text-lg mb-3 border-b border-gray-400">STAFF NOTES</h3>
          <div className="border border-gray-400 p-4 min-h-20 bg-gray-50">
            <p className="whitespace-pre-wrap">{printData.data.eviction_notes}</p>
          </div>
        </div>
      )}

      <div className="mt-6 bg-yellow-50 border-2 border-yellow-400 p-4">
        <h3 className="font-bold text-lg text-yellow-900 mb-2">⚠️ LEGAL NOTICE TO RESIDENT</h3>
        <div className="text-sm text-yellow-900 space-y-2">
          <p><strong>YOU HAVE BEEN EVICTED FROM THIS SHELTER FACILITY.</strong></p>
          <p>• You must vacate the premises immediately</p>
          <p>• You are prohibited from returning to this property</p>
          <p>• You are not eligible for readmission for 30 days from this date</p>
          <p>• Future readmission requires administrator approval</p>
          <p>• Trespassing after eviction may result in legal action</p>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-bold text-lg mb-3 border-b border-gray-400">PERSONAL PROPERTY</h3>
        <div className="border border-gray-400 p-4 bg-gray-50">
          <p className="text-sm">Personal belongings must be claimed within <strong>7 days</strong> of this eviction date. 
          Unclaimed items will be disposed of per shelter policy. Contact shelter administration to arrange pickup.</p>
          <div className="mt-3">
            <p><strong>Claim Deadline:</strong> {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
            <p><strong>Contact:</strong> Shelter Administration</p>
          </div>
        </div>
      </div>

      <div className="mt-8 border-t-2 border-black pt-4">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="border-b border-gray-400 pb-1 mb-2">Staff Signature</p>
            <p className="text-sm text-gray-600">Date: {new Date().toLocaleDateString()}</p>
            <p className="text-sm text-gray-600">Name: {printData.data.staff_name}</p>
          </div>
          <div>
            <p className="border-b border-gray-400 pb-1 mb-2">Supervisor Signature</p>
            <p className="text-sm text-gray-600">Date: _______________</p>
            <p className="text-sm text-gray-600">Name: _______________</p>
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-600 border border-gray-300 p-3">
        <p><strong>SHELTER ADMINISTRATION:</strong> This eviction notice must be retained in the resident's permanent file. 
        A copy must be provided to the resident. Document delivery method and retain proof of service. 
        Follow all local and state regulations regarding tenant rights and eviction procedures.</p>
      </div>

      <div className="mt-4 text-xs text-gray-600 text-center">
        <p><strong>RE-ENTRY ELIGIBILITY DATE:</strong> {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
        <p>Administrator approval required for readmission</p>
      </div>
    </div>
  );

  const DepartureForm = () => (
    <div className="print-friendly space-y-6">
      <div className="text-center border-b-2 border-black pb-4 mb-6">
        <h1 className="text-2xl font-bold">RESIDENT DEPARTURE FORM</h1>
        <p className="text-lg mt-2">OFFICIAL RECORD - KEEP ON FILE</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="font-bold text-lg mb-3 border-b border-gray-400">RESIDENT INFORMATION</h3>
          <div className="space-y-2">
            <p><strong>Name:</strong> {printData.resident.first_name} {printData.resident.last_name}</p>
            <p><strong>Intake Date:</strong> {formatDate(printData.resident.intake_date)}</p>
            <p><strong>Length of Stay:</strong> {printData.resident.stay_length_days} days</p>
            <p><strong>Room Assignment:</strong> {printData.resident.room_id || 'Not Assigned'}</p>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-lg mb-3 border-b border-gray-400">DEPARTURE DETAILS</h3>
          <div className="space-y-2">
            <p><strong>Departure Date:</strong> {printData.data.departure_date ? new Date(printData.data.departure_date).toLocaleString() : 'Not recorded'}</p>
            <p><strong>Staff Member:</strong> {printData.data.staff_name || 'Not recorded'}</p>
            <p><strong>Departure Type:</strong> {printData.data.departure_reason?.replace('_', ' ').toUpperCase() || 'Not specified'}</p>
            <p><strong>Next Eligible Date:</strong> {printData.data.readmission_date ? new Date(printData.data.readmission_date).toLocaleDateString() : 'Not calculated'}</p>
          </div>
        </div>
      </div>

      {printData.data.forwarding_address && (
        <div className="mt-6">
          <h3 className="font-bold text-lg mb-3 border-b border-gray-400">FORWARDING ADDRESS</h3>
          <div className="border border-gray-400 p-4 min-h-20 bg-gray-50">
            <p className="whitespace-pre-wrap">{printData.data.forwarding_address}</p>
          </div>
        </div>
      )}

      <div className="mt-6">
        <h3 className="font-bold text-lg mb-3 border-b border-gray-400">DESTINATION SHELTER TYPE</h3>
        <div className="border border-gray-400 p-4 bg-gray-50">
          <p>{printData.data.destination_shelter_type?.replace('_', ' ').toUpperCase()}</p>
        </div>
      </div>

      {printData.data.departure_notes && (
        <div className="mt-6">
          <h3 className="font-bold text-lg mb-3 border-b border-gray-400">DEPARTURE NOTES</h3>
          <div className="border border-gray-400 p-4 min-h-20 bg-gray-50">
            <p className="whitespace-pre-wrap">{printData.data.departure_notes}</p>
          </div>
        </div>
      )}

      <div className="mt-8 border-t-2 border-black pt-4">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="border-b border-gray-400 pb-1 mb-2">Staff Signature</p>
            <p className="text-sm text-gray-600">Date: {new Date().toLocaleDateString()}</p>
          </div>
          <div>
            <p className="border-b border-gray-400 pb-1 mb-2">Resident Signature (if available)</p>
            <p className="text-sm text-gray-600">Date: _______________</p>
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-600 border border-gray-300 p-3">
        <p><strong>30-DAY POLICY:</strong> This resident is not eligible for readmission for 30 days from departure date. 
        Readmission eligibility begins on the date shown above. Keep this form on file as per shelter policies.</p>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="no-print">
          <div className="flex items-center justify-between">
            <DialogTitle>
              {printData.type === 'ban' ? 'Ban Notice - Ready to Print' : 
               printData.type === 'eviction' ? 'Eviction Notice - Ready to Print' :
               'Departure Form - Ready to Print'}
            </DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        {/* Print Styles */}
        <style jsx>{`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-content, .print-content * {
              visibility: visible;
            }
            .print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: white;
              padding: 20px;
              font-family: Arial, sans-serif;
              font-size: 12pt;
              line-height: 1.4;
              color: black;
            }
            .no-print {
              display: none !important;
            }
            h1, h2, h3 {
              color: black !important;
              margin-top: 0;
            }
            .signature-line {
              border-bottom: 1px solid black;
              width: 200px;
              display: inline-block;
              margin: 0 10px;
            }
          }
          @page {
            margin: 0.75in;
            size: letter;
          }
        `}</style>

        <div className="space-y-4">
          <div className="no-print flex space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Button 
              onClick={handlePrint} 
              disabled={isPrinting}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400"
            >
              <Printer className="h-4 w-4 mr-2" />
              {isPrinting ? 'Printing...' : 'Print Form'}
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <p className="text-sm text-blue-700 self-center ml-4">
              {isPrinting ? 'Please wait - sending to printer...' : 'This form will be saved to your records automatically.'}
            </p>
          </div>

          {renderForm()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintableForm;